// Dexie (IndexedDB) — fila offline de pontos.
//
// Schema enxuto, alinhado ao RegistrarPontoSchema do @app-ponto/shared
// (RegistroTipo lowercase: entrada/saida/intervalo_in/intervalo_out/extra).
//
// Tabelas:
//   - pontos_pendentes:  registros locais aguardando sincronização
//   - pontos_enviados:   registros confirmados pelo servidor (cache de leitura)
//   - pontos_quarentena: registros que falharam 10+ vezes no sync (v3)
//   - geofences_cache:   cercas resolvidas do funcionário (pré-check offline, v3)

import Dexie, { type Table } from 'dexie';
import type { RegistroTipo, RegistroStatus, GeofenceTipo } from '@app-ponto/shared';

// Backoff exponencial entre tentativas de sync: 30s, 2min, 10min, 1h.
const BACKOFF_MS = [30_000, 120_000, 600_000, 3_600_000] as const;
export const MAX_TENTATIVAS_SYNC = 10;
export const GEOFENCE_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

export interface PontoPendente {
  id: string;
  tipo: RegistroTipo;
  timestampDevice: string;
  latitude?: number;
  longitude?: number;
  accuracyMetros?: number;
  biometriaScore?: number;
  biometriaModelo?: string;
  fotoCapturadaBase64?: string;
  justificativa?: string;
  criadoEm: string;
  tentativas: number;
  ultimoErro?: string;
  // ISO — próxima tentativa permitida (backoff); ausente = pode tentar já
  proximaTentativaEm?: string;
}

// Verdade do servidor sobre o registro (retornada no ack do sync).
export interface RegistroServidor {
  id: string;
  status: RegistroStatus | string;
  motivoStatus?: string | null;
  dentroGeofence?: boolean | null;
  distanciaMetros?: number | null;
  biometriaVerificadaServidor?: boolean;
  biometriaScoreServidor?: number | null;
  codigoVerificador?: string | null;
}

export interface PontoEnviado {
  id: string;
  tipo: RegistroTipo;
  timestampDevice: string;
  latitude?: number;
  longitude?: number;
  accuracyMetros?: number;
  biometriaScore?: number;
  biometriaModelo?: string;
  fotoCapturadaBase64?: string;
  justificativa?: string;
  criadoEm: string;
  tentativas: number;
  sincronizadoEm: string;
  statusServidor: RegistroStatus | string;
  registroServidor?: RegistroServidor;
}

export interface PontoQuarentena extends Omit<PontoPendente, 'proximaTentativaEm'> {
  quarentenaEm: string;
}

export interface GeofenceCacheItem {
  id: string;
  nome: string;
  tipo: GeofenceTipo | string;
  latitudeCentro?: number | null;
  longitudeCentro?: number | null;
  raioMetros?: number | null;
  vertices?: Array<{ latitude: number; longitude: number }> | null;
  cachedEm: string;
}

class AppPontoLocalDB extends Dexie {
  pontos_pendentes!: Table<PontoPendente, string>;
  pontos_enviados!: Table<PontoEnviado, string>;
  pontos_quarentena!: Table<PontoQuarentena, string>;
  geofences_cache!: Table<GeofenceCacheItem, string>;

  constructor() {
    super('app-ponto');
    this.version(1).stores({
      pontos_pendentes: 'id, criadoEm, tentativas, tipo',
      pontos_enviados: 'id, sincronizadoEm, timestampDevice, statusServidor',
    });
    // v2: adiciona index timestampDevice no pontos_pendentes (Historico orderBy)
    this.version(2).stores({
      pontos_pendentes: 'id, criadoEm, tentativas, tipo, timestampDevice',
      pontos_enviados: 'id, sincronizadoEm, timestampDevice, statusServidor',
    });
    // v3: só ADICIONA stores novas (quarentena + cache de cercas);
    // índices existentes intactos.
    this.version(3).stores({
      pontos_pendentes: 'id, criadoEm, tentativas, tipo, timestampDevice',
      pontos_enviados: 'id, sincronizadoEm, timestampDevice, statusServidor',
      pontos_quarentena: 'id, quarentenaEm, timestampDevice',
      geofences_cache: 'id, cachedEm',
    });
  }
}

export const localDb = new AppPontoLocalDB();

export interface EnfileirarInput {
  id: string;
  tipo: RegistroTipo;
  timestampDevice: string;
  latitude?: number;
  longitude?: number;
  accuracyMetros?: number;
  biometriaScore?: number;
  biometriaModelo?: string;
  fotoCapturadaBase64?: string;
  justificativa?: string;
}

export async function enfileirar(reg: EnfileirarInput): Promise<PontoPendente> {
  if (!reg.id) throw new Error('enfileirar: id (UUID v4) obrigatório');
  const item: PontoPendente = {
    ...reg,
    criadoEm: new Date().toISOString(),
    tentativas: 0,
  };
  await localDb.pontos_pendentes.put(item);
  return item;
}

/**
 * Próximos pendentes ELEGÍVEIS (respeita backoff: pula itens cuja
 * proximaTentativaEm ainda está no futuro).
 */
export async function proximosPendentes(limit = 50): Promise<PontoPendente[]> {
  const agora = new Date().toISOString();
  const rows = await localDb.pontos_pendentes.orderBy('criadoEm').toArray();
  return rows
    .filter((r) => !r.proximaTentativaEm || r.proximaTentativaEm <= agora)
    .slice(0, limit);
}

export async function contarPendentes(): Promise<number> {
  return localDb.pontos_pendentes.count();
}

export async function marcarEnviado(
  id: string,
  statusServidor: RegistroStatus | string,
  registroServidor?: RegistroServidor,
): Promise<void> {
  await localDb.transaction(
    'rw',
    localDb.pontos_pendentes,
    localDb.pontos_enviados,
    async () => {
      const row = await localDb.pontos_pendentes.get(id);
      if (!row) return;
      const { proximaTentativaEm: _omit, ...rest } = row;
      const enviado: PontoEnviado = {
        ...rest,
        sincronizadoEm: new Date().toISOString(),
        statusServidor,
        ...(registroServidor ? { registroServidor } : {}),
      };
      await localDb.pontos_enviados.put(enviado);
      await localDb.pontos_pendentes.delete(id);
    },
  );
}

/**
 * Registra falha de sync: tentativas++, agenda backoff exponencial
 * (30s → 2min → 10min → 1h) e, após MAX_TENTATIVAS_SYNC tentativas,
 * move o registro para a quarentena.
 */
export async function marcarErro(id: string, erro: string): Promise<void> {
  await localDb.transaction(
    'rw',
    localDb.pontos_pendentes,
    localDb.pontos_quarentena,
    async () => {
      const row = await localDb.pontos_pendentes.get(id);
      if (!row) return;
      const tentativas = row.tentativas + 1;
      if (tentativas >= MAX_TENTATIVAS_SYNC) {
        const { proximaTentativaEm: _omit, ...rest } = row;
        await localDb.pontos_quarentena.put({
          ...rest,
          tentativas,
          ultimoErro: erro,
          quarentenaEm: new Date().toISOString(),
        });
        await localDb.pontos_pendentes.delete(id);
        return;
      }
      const delay = BACKOFF_MS[Math.min(tentativas - 1, BACKOFF_MS.length - 1)];
      await localDb.pontos_pendentes.put({
        ...row,
        tentativas,
        ultimoErro: erro,
        proximaTentativaEm: new Date(Date.now() + delay).toISOString(),
      });
    },
  );
}

export async function listarQuarentena(): Promise<PontoQuarentena[]> {
  return localDb.pontos_quarentena.orderBy('timestampDevice').reverse().toArray();
}

/** Devolve um item da quarentena para a fila (tentativas zeradas). */
export async function reencaminharQuarentena(id: string): Promise<void> {
  await localDb.transaction(
    'rw',
    localDb.pontos_quarentena,
    localDb.pontos_pendentes,
    async () => {
      const row = await localDb.pontos_quarentena.get(id);
      if (!row) return;
      const { quarentenaEm: _omit, ...rest } = row;
      await localDb.pontos_pendentes.put({
        ...rest,
        tentativas: 0,
        ultimoErro: undefined,
      });
      await localDb.pontos_quarentena.delete(id);
    },
  );
}

// ---------------------------------------------------------------------------
// Cache local das cercas do funcionário (GET /funcionarios/me/geofences)
// ---------------------------------------------------------------------------

export async function salvarGeofencesCache(
  cercas: Omit<GeofenceCacheItem, 'cachedEm'>[],
): Promise<void> {
  const cachedEm = new Date().toISOString();
  await localDb.transaction('rw', localDb.geofences_cache, async () => {
    await localDb.geofences_cache.clear();
    await localDb.geofences_cache.bulkPut(cercas.map((c) => ({ ...c, cachedEm })));
  });
}

export interface GeofencesCacheResult {
  cercas: GeofenceCacheItem[];
  expirado: boolean;
}

export async function lerGeofencesCache(): Promise<GeofencesCacheResult> {
  const cercas = await localDb.geofences_cache.toArray();
  if (cercas.length === 0) return { cercas: [], expirado: true };
  const maisRecente = cercas.reduce(
    (max, c) => (c.cachedEm > max ? c.cachedEm : max),
    cercas[0].cachedEm,
  );
  const expirado =
    Date.now() - new Date(maisRecente).getTime() > GEOFENCE_CACHE_TTL_MS;
  return { cercas, expirado };
}

export interface HistoricoFiltro {
  desde?: string;
  ate?: string;
  tipo?: RegistroTipo;
}

export async function historicoLocal(
  filtro: HistoricoFiltro = {},
): Promise<PontoEnviado[]> {
  let rows = await localDb.pontos_enviados.orderBy('timestampDevice').reverse().toArray();
  if (filtro.desde) rows = rows.filter((r) => r.timestampDevice >= filtro.desde!);
  if (filtro.ate) rows = rows.filter((r) => r.timestampDevice <= filtro.ate!);
  if (filtro.tipo) rows = rows.filter((r) => r.tipo === filtro.tipo);
  return rows;
}

export async function limparTudo(): Promise<void> {
  await localDb.transaction(
    'rw',
    localDb.pontos_pendentes,
    localDb.pontos_enviados,
    localDb.pontos_quarentena,
    localDb.geofences_cache,
    async () => {
      await localDb.pontos_pendentes.clear();
      await localDb.pontos_enviados.clear();
      await localDb.pontos_quarentena.clear();
      await localDb.geofences_cache.clear();
    },
  );
}
